import { useMemo, useState, useCallback } from 'react';
import { format } from 'date-fns';

/**
 * Hook personalizado para converter agendamentos para o formato de eventos do React Big Calendar
 * 
 * @param {Array} appointments - Lista de agendamentos vindos do banco de dados
 * @param {Array} resources - Lista de recursos (profissionais) disponíveis
 * @returns {Array} - Lista de eventos formatados para o React Big Calendar
 */
export const useTimelineEvents = ({ appointments, professionals, resources }) => {
  // Reduzir estados para manter apenas o que é necessário para o novo modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Função para selecionar um evento
  const handleSelectEvent = useCallback((event) => {
    // Capturar as dimensões e posição do elemento para posicionar o tooltip
    // Esta parte é essencial para o funcionamento correto do tooltip no desktop
    try {
      // Verificar se o evento do DOM existe
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        // Adicionar propriedades de posicionamento ao evento
        event = {
          ...event,
          rectTop: rect.top + window.scrollY,
          rectRight: rect.right + window.scrollX,
          rectBottom: rect.bottom + window.scrollY,
          rectLeft: rect.left + window.scrollX
        };
      }
    } catch (error) {
      console.warn('Erro ao capturar dimensões do evento:', error);
    }
    
    // Definir o evento selecionado
    setSelectedEvent(event);
  }, []);
  
  // Função para fechar o tooltip
  const closeTooltip = useCallback(() => {
    setSelectedEvent(null);
  }, []);
  
  // Estilo para os eventos (mantendo compatibilidade)
  const eventStyleGetter = useCallback((event) => {
    // Encontrar o recurso correspondente para obter a cor do profissional
    let resource = resources.find(r => r.id === event.resourceId);
    
    // Se não encontrar um recurso e o resourceId for "any", usar um recurso default
    // Isso resolve o erro "Recurso não encontrado para o evento: any"
    if (!resource && event.resourceId === 'any') {
      // Usar um recurso padrão para eventos com resourceId "any"
      resource = {
        id: 'any',
        title: 'Geral',
        color: '#6B7280' // Cor cinza neutra para eventos gerais
      };
    } else if (!resource) {
      console.warn("Recurso não encontrado para o evento:", event.resourceId, event);
    }
    
    const backgroundColor = resource?.color || '#4F46E5';
    
    // Estilo base
    const style = {
      backgroundColor: backgroundColor,
      borderRadius: '4px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'block',
      position: 'relative'
    };
    
    // Adicionar estilo baseado no status
    if (event.status === 'canceled') {
      style.backgroundColor = '#EF4444'; // Vermelho para cancelados
      style.textDecoration = 'line-through';
      style.opacity = 0.7;
    } else if (event.status === 'pending') {
      style.backgroundColor = '#F59E0B'; // Âmbar para pendentes
    }
    
    // Determinar classe adicional
    let className = 'rbc-event-custom';
    if (event.hasSetupTime || event.hasFinishTime) {
      className += ' has-additional-time';
    }
    
    // Adicionar classe especial para agendamentos feitos pelo atendente
    if (event.appointment?.source === 'atendente' || event.appointment?.deviceType === 'atendente') {
      className += ' attendant-booked';
      style.borderLeft = '3px solid #ffffff'; // Borda branca à esquerda para destacar
    }
    
    return { style, className };
  }, [resources]);
  
  // Converter agendamentos para o formato esperado pelo Big Calendar
  const events = useMemo(() => {
    console.log("Processando agendamentos:", appointments?.length || 0);
    
    if (!Array.isArray(appointments) || appointments.length === 0) {
      console.log("Nenhum agendamento para processar");
      return [];
    }
    
    // Filtrar agendamentos cancelados antes de processá-los
    const validAppointments = appointments.filter(appointment => 
      !appointment.cancelado
    );
    
    return validAppointments.map(appointment => {
      try {
        // Extrair data e hora
        let appointmentDate;
        
        // Verificar o tipo da data e converter adequadamente
        if (appointment.data instanceof Date) {
          appointmentDate = appointment.data;
        } else if (typeof appointment.data === 'object' && appointment.data.toDate) {
          // Timestamp do Firestore
          appointmentDate = appointment.data.toDate();
        } else if (typeof appointment.data === 'string') {
          // Corrigir problema de fuso horário adicionando T00:00:00 para garantir que a data seja interpretada no dia correto
          appointmentDate = new Date(`${appointment.data.split('T')[0]}T00:00:00`);
        } else {
          console.log('Data inválida para o agendamento:', appointment);
          appointmentDate = new Date(); // Fallback para data atual
        }
        
        // Extrair hora e minuto do horário (formato: "HH:MM")
        const [startHours, startMinutes] = (appointment.horario || '00:00').split(':').map(Number);
        
        // Criar objeto de data/hora de início
        const startDateTime = new Date(appointmentDate);
        startDateTime.setHours(startHours, startMinutes, 0);
        
        // Verificar se a data é válida
        if (isNaN(startDateTime.getTime())) {
          console.log('Data de início inválida para:', appointment);
          return null;
        }
        
        // Calcular horário de término
        const duration = appointment.duracaoEfetiva || appointment.duracaoTotal || 30;
        const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 1000);
        
        // Verificar se a data de término é válida
        if (isNaN(endDateTime.getTime())) {
          console.log('Data de término inválida para:', appointment);
          return null;
        }
        
        // Determinar o recurso (profissional)
        let resourceId = 'any';
        
        // Verificar se o profissional é um objeto ou um ID
        if (appointment.profissional) {
          if (typeof appointment.profissional === 'object' && appointment.profissional.id) {
            resourceId = appointment.profissional.id;
          } else if (typeof appointment.profissional === 'string') {
            resourceId = appointment.profissional;
          }
        } else if (appointment.profissionalId) {
          resourceId = appointment.profissionalId;
        }
        
        // Tempos de preparação e finalização
        const hasSetupTime = appointment.tempoPreparacao && appointment.tempoPreparacao > 0;
        const hasFinishTime = appointment.tempoFinalizacao && appointment.tempoFinalizacao > 0;
        
        // Se existe tempo de preparação, ajustar o horário de início
        let setupStart = null;
        if (hasSetupTime) {
          setupStart = new Date(startDateTime.getTime() - appointment.tempoPreparacao * 60 * 1000);
        }
        
        // Se existe tempo de finalização, criar evento adicional
        let finishEnd = null;
        if (hasFinishTime) {
          finishEnd = new Date(endDateTime.getTime() + appointment.tempoFinalizacao * 60 * 1000);
        }
        
        // Calcular preço total do serviço - pode ser valor direto ou calculado
        let precoTotal = appointment.preco || 0;
        if (appointment.servicos && Array.isArray(appointment.servicos)) {
          precoTotal = appointment.servicos.reduce((total, servico) => {
            return total + (servico.preco || 0);
          }, 0);
        }
        
        // Criar o evento principal
        return {
          id: appointment.id,
          title: appointment.cliente || 'Cliente não informado',
          start: startDateTime,
          end: endDateTime,
          resourceId: resourceId,
          appointment: appointment,
          status: appointment.status || (appointment.cancelado ? 'canceled' : 'confirmed'),
          hasSetupTime,
          hasFinishTime,
          setupStart,
          finishEnd,
          precoTotal
        };
      } catch (error) {
        console.error("Erro ao processar agendamento:", error, appointment);
        return null;
      }
    }).filter(Boolean); // Remover itens nulos
  }, [appointments]);
  
  return {
    events,
    eventStyleGetter,
    selectedEvent,
    handleSelectEvent,
    closeTooltip,
    isSaving,
    setIsSaving
  };
};

export default useTimelineEvents; 